var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { ChevronDown } from '@volkswagen-onehub/icons-core';
import { styled, css } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
import { getAuxillaryColor } from '../helpers/input';
import { InputNotification } from '../helpers/input-notification';
const ANIMATION_FUNCTION = '0.3s cubic-bezier(0.14, 1.12, 0.67, 0.99)';
const selectHeight = '50'; // static size of the input field, we don't have a token to represent this number currently; add in the future if needed
const iconDefaultVariantSize = '24'; // default icon size, 24x24px
// the icon needs to be moved up by the size of the wrapper's bottom padding
const StyledIconWrapper = styled.span.withConfig({ displayName: "StyledIconWrapper", componentId: "sc-jne68a" }) `position:absolute;top:${(parseInt(selectHeight) - parseInt(iconDefaultVariantSize)) / 2}px;right:${({ theme }) => theme.size.static250};display:flex;justify-content:flex-end;pointer-events:none;transition:${({ theme }) => `transform ${ANIMATION_FUNCTION}, color ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};align-items:center;white-space:nowrap;`;
const StyledLabelText = styled.span.withConfig({ displayName: "StyledLabelText", componentId: "sc-62bnde" }) `font-size:${({ theme }) => theme.fonts.label[100].fontSize};width:100%;display:block;margin:0;padding:0;color:${({ isFloating, theme }) => isFloating ? 'transparent' : theme.colors.content.active};pointer-events:none;visibility:${({ isFloating }) => (isFloating ? 'hidden' : 'initial')};max-height:${({ isFloating, theme }) => isFloating ? '0' : `${theme.size.static250}`};transition:${({ theme }) => `max-height ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};`;
const StyledSelect = styled.select.withConfig({ displayName: "StyledSelect", componentId: "sc-131xm3f" }) `appearance:none;box-shadow:none;opacity:unset;pointer-events:auto;height:auto;width:100%;padding:0;padding-top:1px;${({ theme }) => theme.direction === 'rtl'
    ? css `
					padding-left: ${theme.size.static400};
				`
    : css `
					padding-right: ${theme.size.static400};
				`} color:inherit;background:transparent;font-size:${({ theme }) => theme.fonts.copy[200].fontSize};font-family:${({ theme }) => theme.fonts.copy[200].fontFamily};outline:none;border:none;cursor:${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};transition:${({ theme }) => `all ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};&:focus{color:${({ theme }) => theme.colors.content.primary};}option{color:initial;&:disabled{color:revert;}}`;
const StyledLabelTextWithFloating = styled.span.withConfig({ displayName: "StyledLabelTextWithFloating", componentId: "sc-e96ov" }) `transition:${({ theme }) => `all ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};position:absolute;bottom:0;left:0;color:inherit;${({ hasValue, theme }) => {
    // Label-style, above the input
    if (hasValue) {
        return css `
				bottom: 100%;
				cursor: initial;
				font-size: ${theme.fonts.label[100].fontSize};
				padding: 0;
			`;
    }
    // Placeholder style, "inside" the input
    return css `
			bottom: 0;
			cursor: text;
			font-size: ${theme.fonts.copy[200].fontSize};
			padding: ${theme.size.static100} 0;
		`;
}} ${StyledSelect}:focus ~ &{bottom:100%;}`;
const StyledLabel = styled.label.withConfig({ displayName: "StyledLabel", componentId: "sc-1wmcxnr" }) `position:relative;display:flex;flex-direction:column;justify-content:center;gap:2px;height:${selectHeight}px;color:${({ disabled, theme }) => theme.colors.content[disabled ? 'disabled' : 'primary']};pointer-events:none;cursor:${({ disabled }) => (disabled ? 'not-allowed' : 'auto')};padding:0 ${({ theme }) => theme.size.static250};box-shadow:0 0 0 ${({ theme }) => theme.size.border.light} ${getAuxillaryColor};border-radius:${({ theme }) => theme.size.static150};${({ appearance }) => !appearance &&
    css `
			&:focus-within {
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.heavy}
					${({ theme }) => theme.colors.focus.main};
			}
		`} &:focus-within ${StyledLabelTextWithFloating},&:focus-within ${StyledLabelText}{padding:${({ theme }) => theme.size.static0};font-size:${({ theme }) => theme.fonts.label[100].fontSize};}${({ appearance }) => (appearance === 'success' || appearance === 'error') &&
    css `
			box-shadow: 0 0 0 ${({ theme }) => theme.size.border.heavy}
				${getAuxillaryColor};
		`} ${({ hasValue, theme }) => hasValue &&
    css `
			${StyledLabelText} {
				max-height: ${theme.size.static250};
			}
		`} ${({ theme, disabled, appearance }) => !disabled &&
    !appearance &&
    css `
			:hover,
			:focus-within {
				color: ${theme.colors.content.hover};
			}
			:hover {
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.light}
					${({ theme }) => theme.colors.border.hover};
			}
			:hover ${StyledLabelText} {
				color: ${theme.colors.content.hover};
			}
		`} ${({ isFloatingLabel, theme }) => isFloatingLabel &&
    css `
			/* On focus / while typing: Label-style, above the input */
			&:focus-within ${StyledLabelText} {
				max-height: ${theme.size.static250};
			}
		`}`;
const StyledSelectWrapper = styled.div.withConfig({ displayName: "StyledSelectWrapper", componentId: "sc-1jc3d6n" }) `position:relative;`;
export const Select = (props) => {
    const ref = React.createRef();
    const labelRef = React.createRef();
    const { appearance, children, disabled, label, defaultValue, value, message, onFocus, onChange, onBlur, required, name, autoComplete, autoFocus, isFloating } = props, rest = __rest(props, ["appearance", "children", "disabled", "label", "defaultValue", "value", "message", "onFocus", "onChange", "onBlur", "required", "name", "autoComplete", "autoFocus", "isFloating"]);
    const [hasValue, setHasValue] = React.useState(false);
    const [labelHeight, setLabelHeight] = React.useState(0);
    const [isFocused, setIsFocused] = React.useState(false);
    const handleChange = (event) => {
        if (onChange) {
            onChange(event);
        }
        setHasValue(true);
    };
    React.useEffect(() => {
        if (ref.current &&
            ref.current.selectedIndex <= 0 &&
            ref.current.querySelector('option:first-child:disabled')) {
            ref.current.selectedIndex = -1;
        }
        else {
            setHasValue(true);
        }
    }, [ref]);
    React.useEffect(() => {
        var _a;
        setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0);
    }, [labelRef]);
    const handleResize = React.useCallback(() => { var _a; return setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0); }, [labelRef]);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    const handleFocus = (event) => {
        setIsFocused(true);
        if (onFocus) {
            onFocus(event);
        }
    };
    const commonProps = getCommonProps(props);
    const errorMessage = appearance && message ? (React.createElement(InputNotification, { appearance: appearance, text: message, isFocused: isFocused, hideIcon: true })) : undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledLabel, Object.assign({ disabled: disabled, isFloatingLabel: isFloating, appearance: appearance, hasValue: hasValue }, commonProps, rest),
            label && (React.createElement(StyledLabelText, { ref: labelRef, isFloating: isFloating },
                label,
                required && '*')),
            React.createElement(StyledSelectWrapper, null,
                React.createElement(StyledSelect, { autoComplete: autoComplete, disabled: disabled, required: required, ref: ref || undefined, defaultValue: defaultValue, value: value, onChange: handleChange, onFocus: handleFocus, onBlur: onBlur, name: name, autoFocus: autoFocus }, children),
                label && isFloating && (React.createElement(StyledLabelTextWithFloating, { hasValue: hasValue, labelHeight: labelHeight },
                    label,
                    required && '*'))),
            React.createElement(StyledIconWrapper, null,
                React.createElement(ChevronDown, { variant: "default", ariaHidden: true }))),
        errorMessage));
};
